import { Core } from "@faire/design-tokens";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useRetailerFinancial } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerFinancial";
import { NetTermsCard } from "@faire/retailer-visitor-shared/views/SignUp/_components/net-terms/NetTermsCard";
import { useRetailerUpmarket } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useRetailerUpmarket";
import {
  RoundingStrategy,
  moneyOrZero,
  useFormatMoney,
} from "@faire/web--source/common/money";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Money } from "@faire/web--source/ui/Money";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import React from "react";
import styled from "styled-components";

interface IProps {
  overrideNetTermsAmount?: IFaireMoney;
}

const DEFAULT_PERCENTAGE = 10;

export const UpmarketCard: React.FC<IProps> = ({ overrideNetTermsAmount }) => {
  const { formatMoney } = useFormatMoney();
  const { retailer } = useRetailer();
  const { currency: displayCurrency } = useRetailerFinancial();
  const { potentialNetTermsAmount } = useRetailerUpmarket();
  const newNetTermsPercentage = potentialNetTermsAmount?.amount_cents
    ? ((overrideNetTermsAmount?.amount_cents ?? 0) /
        potentialNetTermsAmount.amount_cents) *
      100
    : 0;

  return (
    <NetTermsCard>
      <Typography variant="paragraphSansMedium">{retailer?.name}</Typography>
      <Spacer height="9x" />
      <NetTermsBar
        $newPercentage={
          overrideNetTermsAmount ? newNetTermsPercentage : DEFAULT_PERCENTAGE
        }
      />
      <Spacer height="1x" />
      <Row justify="space-between">
        <Column>
          <Typography variant="paragraphSansMedium">
            <Money
              money={moneyOrZero(
                overrideNetTermsAmount ?? retailer?.terms_limit,
                displayCurrency
              )}
              roundingStrategy={RoundingStrategy.DROP_CENTS}
            />
          </Typography>
          <Typography variant="labelSansRegular">
            <StrictLocalMsg
              defaultMessage="Available"
              description={{
                text: "Subtitle current available payment terms",
                img: "https://cdn.faire.com/fastly/bed9905ffc4b0d9c093098a7e0030e4b285d7245588800e3c1ce0c45b79a8ba2.png",
              }}
            />
          </Typography>
        </Column>
        <Column style={{ textAlign: "end" }}>
          <Typography variant="paragraphSansMedium">
            <StrictLocalMsg
              defaultMessage="{netTermsPotentialMoney}+"
              description={{
                text: "Header title letting retailer know they have payment terms",
                img: "https://cdn.faire.com/fastly/bed9905ffc4b0d9c093098a7e0030e4b285d7245588800e3c1ce0c45b79a8ba2.png",
              }}
              values={{
                netTermsPotentialMoney: formatMoney({
                  money: potentialNetTermsAmount,
                  roundingStrategy: RoundingStrategy.ROUND,
                  omitCentsIfZero: true,
                }),
              }}
            />
          </Typography>
          <Typography variant="labelSansRegular">
            <StrictLocalMsg
              defaultMessage="Potential"
              description={{
                text: "Subtitle potential payment terms",
                img: "https://cdn.faire.com/fastly/bed9905ffc4b0d9c093098a7e0030e4b285d7245588800e3c1ce0c45b79a8ba2.png",
              }}
            />
          </Typography>
        </Column>
      </Row>
    </NetTermsCard>
  );
};

const NetTermsBar = styled.div<{ $newPercentage: number }>`
  transition: all 0.3s linear;
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    ${Core.text.default} 0%,
    ${Core.text.default} ${({ $newPercentage }) => $newPercentage}%,
    white ${({ $newPercentage }) => $newPercentage}%,
    white 90%
  );
`;
